// VOICE-CHOICES: the voice catalogue's prices, the company's default voice and a voice session
// segment's usage (voice-catalogue.ts). Which voices are *available* (a premium voice needs its
// keys) is the voice service's knowledge (`GET /voice/v1/voices`); this side prices every voice
// of the catalogue and records what a session used.
import { z } from "zod";
import { Uuid } from "../primitives";
import { Mood, TtsEngineSchema, VoiceId, VoiceTier } from "../voice-catalogue";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";

export const VoiceOption = z
  .object({
    id: z.string(),
    name: z.string(),
    sample: z.string(),
    tier: VoiceTier,
    /** The engines that may speak it, in order (the voice service uses the first configured). */
    engines: z.array(TtsEngineSchema),
    /** What a minute bills now (provider cost × markup, or the free rate), in micros. */
    rate_micros_per_minute: z.int().min(0),
    /** "included" or "$0.028 per minute". */
    price_note: z.string(),
  })
  .meta({ id: "VoiceOption" });

export const VoiceSettings = z
  .object({
    voices: z.array(VoiceOption),
    /** The owner's default (null: Aiden, else the first voice offered). */
    default_voice_id: z.string().nullable(),
    /** The caller's own choice (`me/prefs/voice_front.voice_id`), null when never set. */
    my_voice_id: z.string().nullable(),
    /** The caller's mood (`me/prefs/voice_front.mood`; default Plain). */
    my_mood: Mood,
    /** The moods, fastest first, each with its speed indicator against Plain. */
    moods: z.array(
      z.object({
        id: Mood,
        label: z.string(),
        /** "fastest", "slightly slower", "slower", "slowest". */
        speed: z.string(),
        speed_ratio: z.number(),
        /** false: an estimate, not yet measured against the gateway. */
        measured: z.boolean(),
      }),
    ),
    /** Shown when a mood other than Plain is picked. */
    mood_note: z.string(),
    bill_voice_usage: z.boolean(),
    /** The caller may change the company default (owners, admins, the agency). */
    can_set_default: z.boolean(),
  })
  .meta({ id: "VoiceSettings" });
export type VoiceSettings = z.infer<typeof VoiceSettings>;

export const VoiceSettingsPut = z
  .strictObject({ default_voice_id: VoiceId.nullable() })
  .meta({ id: "VoiceSettingsPut" });

export const VoiceUsageRecord = z
  .strictObject({
    /** The voice session (the Live tab's session id). */
    session_id: Uuid,
    /** The segment of that session (16 hex): the same segment is booked once. */
    segment_key: z
      .string()
      .max(16)
      .regex(/^[0-9a-f]{16}$/),
    voice_id: VoiceId,
    /** The engine that spoke it (must be one of the voice's): a premium voice is priced by it. */
    engine: TtsEngineSchema,
    /** How long the segment was connected for voice, in seconds. */
    seconds: z.int().min(0).max(86_400),
    started_at: z.iso.datetime(),
  })
  .meta({ id: "VoiceUsageRecord" });
export type VoiceUsageRecord = z.infer<typeof VoiceUsageRecord>;

export const VoiceSessionOpen = z
  .strictObject({
    session_id: Uuid,
    /** The person who connected (a person member of the company). */
    membership_id: Uuid,
  })
  .meta({ id: "VoiceSessionOpen" });

export const VoiceSessionOpened = z
  .object({ session_id: z.string(), started_at: z.string(), opened: z.boolean() })
  .meta({ id: "VoiceSessionOpened" });

export const VoiceUsageResult = z
  .object({
    status: z.enum(["posted", "duplicate", "nothing"]),
    cost_event_id: z.string().nullable(),
    rate_micros_per_minute: z.int().min(0),
    billed_micros: z.int().min(0),
  })
  .meta({ id: "VoiceUsageResult" });

export const voiceRoutes = {
  "voice.settings.get": defineRoute({
    feature: "core",
    id: "voice.settings.get",
    method: "get",
    path: `${base}/voice/settings`,
    tag: "me",
    summary: "The voice catalogue with its prices, the company default and the caller's choice",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: VoiceSettings },
    errors: [],
    emits: [],
  }),
  "voice.settings.put": defineRoute({
    feature: "core",
    id: "voice.settings.put",
    method: "patch",
    path: `${base}/voice/settings`,
    tag: "me",
    summary: "Set the company's default voice (owners, admins, the agency)",
    access: "manager",
    params: CompanyParams,
    body: VoiceSettingsPut,
    response: { status: 200, schema: VoiceSettings },
    errors: [],
    emits: ["company.updated"],
  }),
  "voice.sessions.open": defineRoute({
    feature: "core",
    id: "voice.sessions.open",
    method: "post",
    path: `${base}/voice/sessions`,
    tag: "me",
    summary: "Open a voice session the voice service hosts (the service's credential only)",
    description:
      "Called by the voice service with its own credential when a person connects for voice " +
      "(after it checked their membership with their credential). The session starts at the " +
      "API's clock; opening it again is a no-op. A person's credential is refused (401).",
    access: "voice_service",
    params: CompanyParams,
    body: VoiceSessionOpen,
    response: { status: 200, schema: VoiceSessionOpened },
    errors: [],
    emits: ["voice_session.opened"],
  }),
  "voice.usage.record": defineRoute({
    feature: "core",
    id: "voice.usage.record",
    method: "post",
    path: `${base}/voice/usage`,
    tag: "me",
    summary: "Record one voice session segment's usage at its voice's rate (once per segment)",
    description:
      "The voice service reports a segment with its own credential when it ends; a person's " +
      "credential is refused (401). The session must be one the service opened in this company " +
      "and the segment must fit its timeline (it starts inside the session, and the session's " +
      "booked seconds never exceed the time since it opened). Booked once per (session, segment) " +
      "as `voice` usage, priced at the voice's tier rate.",
    access: "voice_service",
    params: CompanyParams,
    body: VoiceUsageRecord,
    response: { status: 200, schema: VoiceUsageResult },
    errors: [],
    emits: ["cost.posted"],
  }),
} as const;
